import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import CheckIcon from "@mui/icons-material/Check";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { toast } from "react-toastify";

import {
  getChoices,
  BRAND_VIOLET,
  usePrefersReducedMotion,
  vibrate,
  useImageBand,
  CONTEXT_BAND_MIN,
  CONTEXT_BAND_MAX,
} from "./cardCommon";
import { useFitToHeight } from "./useFitToHeight";

const POST_VOTE_AUTO_ADVANCE_MS = 3000;

/**
 * Quick-poll card. No right answer — purely engagement. Tap a choice,
 * see vote-percentage bars fill in across the options, auto-advance
 * after 3 seconds.
 *
 * Percentages come from `existingResponse.vote_counts` (a `{idx: count}`
 * dict aggregated server-side). Displayed only when admins enabled the
 * "Show vote percentages after answering" toggle (`content.show_percentages`,
 * defaults to true). A 0-vote denominator falls back to the bare
 * checkmark UI — never divide by zero.
 */
export function PollCard({
  card,
  isActive,
  shouldLoadMedia = true,
  existingResponse,
  onAdvance,
  onSubmitResponse,
  language,
  // When false (storyboard preview tiles), the card is display-only: choices
  // can't be voted on. The preview marks the subtree inert/aria-hidden, but
  // that isn't reliable, so we gate behavior here too.
  interactive = true,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const content = card.content || {};
  const question = content.question || "";
  const choices = getChoices(card);
  const headerImageUrl = shouldLoadMedia ? card.media?.url || null : null;
  // Aspect-driven image band (40-55%), matching the split card's behavior but
  // capped lower to leave room for the question + answers below.
  const { band, loaded, imgRef, handleLoad } = useImageBand(
    headerImageUrl,
    CONTEXT_BAND_MIN,
    CONTEXT_BAND_MAX,
  );
  // Default true: editor surface defaults the toggle on, and the value
  // only persists in `content_json` when admins explicitly flip it off.
  const showPercentages = content.show_percentages !== false;
  const voteCounts = existingResponse?.vote_counts || null;
  const totalVotes = voteCounts
    ? Object.values(voteCounts).reduce((sum, n) => sum + (n || 0), 0)
    : 0;
  const showBars = showPercentages && voteCounts && totalVotes > 0;

  // Pre-fill if the user already voted (resume flow). selectedIndex is the
  // single chosen index — polls in v1 are single-select.
  //
  // Validate the restored index against the current choice count.
  // Responses are preserved across deck edits, so an admin who deleted
  // or reordered choices can leave an old `selected_indices` value that
  // points beyond the current array. Without the bounds check, the
  // card would mark itself as already voted (chip highlight wrong /
  // missing, auto-advance still fires) even though no current option
  // is actually selected. Treat out-of-range as not-yet-voted so the
  // user gets a real chance to vote on the new option set.
  const rawInitialSelected =
    existingResponse?.selected_indices?.[0] ?? null;
  const initialSelected =
    typeof rawInitialSelected === "number" &&
    rawInitialSelected >= 0 &&
    rawInitialSelected < choices.length
      ? rawInitialSelected
      : null;
  const [selectedIndex, setSelectedIndex] = React.useState(initialSelected);
  const [submitting, setSubmitting] = React.useState(false);
  // Distinct from `selectedIndex`: only flips true after the BE accepts
  // the vote. The auto-advance keys off this so a slow / failing
  // network can't carry the user past a card whose response was never
  // persisted (which would otherwise fail the section-completion gate
  // and bounce them back unexpectedly).
  const [votePersisted, setVotePersisted] = React.useState(
    initialSelected !== null,
  );

  const hasVoted = selectedIndex !== null && selectedIndex !== undefined;

  // Shrink the question + options to fit the card height when a dense card would
  // otherwise clip the lower options (the player can't scroll). Only engages on
  // the rare overflowing card; short cards stay at scale 1. Always on —
  // including the editor preview, which renders this real card at full player
  // size via ScaledCardPreview, so the fit matches the live player exactly.
  // headerImageUrl is a dep (not just band): media loads lazily as a slide
  // nears selection, so the band can MOUNT after the first fit. For common
  // landscape images the band sits at CONTEXT_BAND_MIN before and after, so
  // `band` alone never changes — without the URL dep a dense card would stay
  // fit-at-full-height and clip under the newly added image.
  const { cardRef, contentRef } = useFitToHeight({
    deps: [hasVoted, showBars, band, headerImageUrl, question, choices.length],
  });

  // After-vote auto-advance — gated on persistence, not selection.
  React.useEffect(() => {
    if (!isActive || !votePersisted) return undefined;
    const t = setTimeout(() => {
      onAdvance && onAdvance();
    }, POST_VOTE_AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [isActive, votePersisted, onAdvance]);

  const handleChoose = async (idx) => {
    if (hasVoted || submitting) return;
    setSubmitting(true);
    setSelectedIndex(idx);
    vibrate(12);
    try {
      await onSubmitResponse([idx]);
      // Mark as persisted only after the BE has accepted the vote.
      // The auto-advance effect above will then schedule.
      setVotePersisted(true);
    } catch (_) {
      // Roll back on error so the user can retry, and surface a toast
      // — silent rollback would leave the user wondering why their tap
      // didn't take. votePersisted stays false so no auto-advance fires.
      setSelectedIndex(null);
      const tLocal = (en, es, zh) =>
        language === "spanish" ? es : language === "chinese" ? zh : en;
      toast.error(
        tLocal(
          "Couldn't submit your vote. Please try again.",
          "No se pudo enviar tu voto. Inténtalo de nuevo.",
          "无法提交投票。请再试一次。",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const thankYouCopy = (() => {
    const lang = (language || "").toLowerCase();
    if (lang === "spanish") return "¡Gracias por compartir!";
    if (lang === "chinese") return "感谢您的分享！";
    return "Thanks for sharing!";
  })();

  return (
    <div
      ref={cardRef}
      className="relative w-full h-full bg-white flex flex-col select-none"
    >
      {/* Optional context image at top. Aspect-driven band (40-55% of card
          height), so the same photo reads consistently with the split card;
          content takes the rest. Without an image the content fills the
          full card. */}
      {headerImageUrl && (
        <div
          className="relative bg-slate-200 overflow-hidden transition-[flex-basis] duration-300"
          style={{ flexBasis: `${band * 100}%`, flexGrow: 0, flexShrink: 1, minHeight: 0 }}
        >
          {/* Skeleton while the photo decodes — no black flash / hard pop-in;
              the image fades in over it on load (mirrors the split card). */}
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 animate-pulse">
              <ImageOutlinedIcon className="text-slate-400" style={{ fontSize: 40 }} />
            </div>
          )}
          <img
            ref={imgRef}
            src={headerImageUrl}
            alt=""
            className="w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: loaded ? 1 : 0 }}
            draggable={false}
            loading="eager"
            decoding="async"
            onLoad={handleLoad}
          />
        </div>
      )}

      {/* Content-first: this area takes the height it needs and the image
          band above yields space to it (the image can shrink below its
          aspect height on a crowded card). No internal scroll — that would
          swallow the deck's swipe gesture — so the length-capped question +
          choices are sized to always fit.
          Top padding clears the player's top chrome (progress bar + close X)
          only when there's no image — `pt-14` (56px, 4px to spare). With an
          image band above, the image already clears the chrome, so the
          content sits tighter (`pt-6`). */}
      <div
        ref={contentRef}
        // Root font size for the scaled content. var(--fit-scale) is driven by
        // useFitToHeight (1 = unchanged). Question/options size in em off this,
        // so a sub-1 scale reflows them; at scale 1 it's pixel-identical.
        style={{ fontSize: "calc(1rem * var(--fit-scale, 1))" }}
        className={`grow shrink-0 flex flex-col px-6 ${
          headerImageUrl ? "pt-6" : "pt-14"
        } pb-6`}
      >
        <h2
          className="font-bold text-gray-900 leading-tight"
          // 1.25em = text-xl at scale 1; mb-6 (1.5rem) as 1.5em so spacing
          // also tightens as the card shrinks.
          style={{ fontSize: "1.25em", marginBottom: "1.5em" }}
        >
          {question}
        </h2>

        <div className="flex flex-col gap-2">
          {choices.map((choice, idx) => {
            const isSelected = idx === selectedIndex;
            const choiceCount = voteCounts ? voteCounts[String(idx)] || 0 : 0;
            const pct =
              showBars && totalVotes > 0
                ? Math.round((choiceCount / totalVotes) * 100)
                : 0;

            // Tinted-chip treatment matching CompleteQuizModal — slate
            // idle, violet selected. The bar visualization (post-vote)
            // overlays an animated fill on top of the same chip but uses
            // a slate-100 base so the bar reads cleanly.
            const bgClass = showBars
              ? "bg-slate-100"
              : isSelected
              ? "bg-violet-50"
              : "bg-slate-50";
            const textClass = showBars
              ? "text-slate-900"
              : isSelected
              ? "text-violet-900"
              : "text-slate-700";
            const borderClass = showBars
              ? "border-transparent"
              : isSelected
              ? "border-violet-300"
              : "border-slate-200";
            const iconWrapClass = isSelected
              ? "bg-violet-600"
              : "border-2 border-slate-300 bg-white";
            const iconNode = isSelected ? (
              <CheckIcon style={{ fontSize: 14, color: "white" }} />
            ) : null;

            return (
              <div
                key={idx}
                role={interactive ? "button" : undefined}
                tabIndex={interactive && !hasVoted && !submitting ? 0 : -1}
                aria-disabled={!interactive || hasVoted || submitting}
                aria-pressed={interactive ? isSelected : undefined}
                onClick={() => {
                  if (interactive && !hasVoted && !submitting) handleChoose(idx);
                }}
                onKeyDown={(e) => {
                  if (
                    interactive &&
                    (e.key === "Enter" || e.key === " ") &&
                    !hasVoted &&
                    !submitting
                  ) {
                    e.preventDefault();
                    handleChoose(idx);
                  }
                }}
                // A div (role="button"), not a native <button>: in this Embla
                // swipe deck the drag initiates from plain elements (like the
                // image) but NOT from native buttons, so the choices were a
                // dead zone for swiping. A div drags through like the image
                // while keeping click + keyboard activation. (whileTap is also
                // omitted — framer's press gesture would re-capture the
                // pointer and swallow the drag.)
                className={[
                  "relative w-full text-left rounded-lg p-3 border flex items-start gap-3 overflow-hidden transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-500",
                  interactive && !hasVoted && !submitting ? "cursor-pointer" : "",
                  bgClass,
                  borderClass,
                  // Without bars, dim unselected post-vote options. With
                  // bars, the bar widths carry the emphasis so opacity
                  // treatment is unnecessary.
                  !showBars && hasVoted && !isSelected ? "opacity-60" : "",
                ].join(" ")}
              >
                {/* Percentage fill — animates from 0 to its width on
                    reveal. Selected option fills with violet, others
                    with a soft slate so the user's pick still reads as
                    primary in heavy-vote scenarios. */}
                {showBars && (
                  <motion.div
                    aria-hidden
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{
                      duration: reducedMotion ? 0 : 0.6,
                      delay: reducedMotion ? 0 : 0.1,
                      ease: "easeOut",
                    }}
                    className="absolute inset-y-0 left-0 rounded-lg"
                    style={{
                      backgroundColor: isSelected
                        ? BRAND_VIOLET
                        : "#cbd5e1", // slate-300
                      opacity: isSelected ? 0.18 : 0.35,
                    }}
                  />
                )}

                <motion.div
                  initial={false}
                  animate={
                    isSelected && !reducedMotion && !showBars
                      ? { scale: [0, 1.15, 1] }
                      : { scale: 1 }
                  }
                  transition={{
                    duration: 0.3,
                    type: reducedMotion ? "tween" : "spring",
                    stiffness: 500,
                    damping: 22,
                  }}
                  className={`relative flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${iconWrapClass}`}
                >
                  {iconNode}
                </motion.div>

                <div className="relative flex-1 min-w-0 flex items-center justify-between gap-2">
                  <span
                    // No explicit text size: inherits the scaled root font
                    // (1rem at scale 1 = the old text-base), so options shrink.
                    className={`flex items-center gap-2 min-w-0 font-medium ${textClass}`}
                  >
                    {choice?.emoji && (
                      <span className="shrink-0">{choice.emoji}</span>
                    )}
                    <span className="min-w-0 break-words">
                      {choice?.text || ""}
                    </span>
                  </span>
                  {showBars && (
                    <span
                      className={`shrink-0 text-sm font-semibold tabular-nums ${
                        isSelected ? "text-violet-900" : "text-slate-700"
                      }`}
                    >
                      {pct}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Confirmation copy after vote — swap in a vote-count subtitle
            when bars are showing, since the checkmark is gone. */}
        <AnimatePresence>
          {hasVoted && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.3 }}
              className="mt-6 text-center text-sm text-gray-600"
            >
              {showBars
                ? `${totalVotes} ${totalVotes === 1 ? "vote" : "votes"}`
                : thankYouCopy}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
